/**
 * CommentReplyRow
 *
 * Single reply row rendered under a CommentRow's children list.
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { UserUtils } from '@/domain/utils/userUtils';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { Navigator } from '@/presentation/routing/Navigator';
import type { RootState } from '@/presentation/store';
import { CommentLikeButton } from './CommentLikeButton';
import { CommentMediaThumbnail } from './CommentMediaThumbnail';
import { renderMentionText } from './utils/renderMentionText';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { CommentEntity } from '@/domain/entities/comment/commentEntity';

export interface CommentReplyRowProps {
  reply: CommentEntity;
  highlighted: boolean;
  onReply: (comment: CommentEntity) => void;
  onLike: (comment: CommentEntity, liked: boolean) => void;
}

export const CommentReplyRow: React.FC<CommentReplyRowProps> = ({
  reply,
  highlighted,
  onReply,
  onLike,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const dateFormatters = useDateFormatters();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const isCurrentUser = currentUser?.firebaseId === reply.userId;
  const displayName = isCurrentUser ? l10n.comments.you : reply.userName;
  const replyToName = reply.commentToReply
    ? currentUser?.firebaseId === reply.commentToReply.userId
      ? l10n.comments.you
      : reply.commentToReply.userName
    : null;

  return (
    <div
      {...qaAttr({ id: `comment-reply-${reply.id}` })}
      style={{
        display: 'flex',
        gap: responsive.gap.base,
        paddingTop: responsive.spacing.sm,
        paddingBottom: responsive.spacing.sm,
        backgroundColor: highlighted ? theme.backgroundSecondary : 'transparent',
      }}
    >
      <div
        onClick={() => Navigator.navigateToUserDetails(reply.userId)}
        style={{ cursor: 'pointer', flexShrink: 0 }}
      >
        <CachedImage
          src={UserUtils.generateUserProfileImageUrl(reply.userId)}
          alt={reply.userName}
          style={{
            width: responsive.avatar.small,
            height: responsive.avatar.small,
            borderRadius: responsive.borderRadius.round,
            objectFit: 'cover',
          }}
        />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: responsive.gap.xs }}>
          <span
            onClick={() => Navigator.navigateToUserDetails(reply.userId)}
            style={{
              fontWeight: responsive.fontWeight.semibold,
              color: theme.text.primary,
              fontSize: responsive.font.sm,
              cursor: 'pointer',
            }}
          >
            {displayName}
          </span>
          {reply.commentToReply && replyToName && (
            <>
              <span
                style={{
                  color: theme.text.secondary,
                  fontSize: responsive.font.sm,
                }}
              >
                {'>'}
              </span>
              <span
                onClick={() => reply.commentToReply && Navigator.navigateToUserDetails(reply.commentToReply.userId)}
                style={{
                  fontWeight: responsive.fontWeight.semibold,
                  color: theme.text.primary,
                  fontSize: responsive.font.sm,
                  cursor: 'pointer',
                }}
              >
                {replyToName}
              </span>
            </>
          )}
        </div>
        <div
          style={{
            color: theme.text.primary,
            fontSize: responsive.font.sm,
            marginTop: responsive.spacing.xxs,
            wordBreak: 'break-word',
          }}
        >
          {renderMentionText(reply.text, theme.exclusive)}
        </div>
        <CommentMediaThumbnail mediaInfo={reply.mediaInfo} commentId={reply.id} />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: responsive.gap.base,
            marginTop: responsive.spacing.xs,
          }}
        >
          <span style={{ fontSize: responsive.font.xs, color: theme.text.secondary }}>
            {dateFormatters.formatDate(reply.createdTimestamp)}
          </span>
          <button
            {...qaAttr({ id: `comment-reply-action-${reply.id}` })}
            onClick={() => onReply(reply)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: theme.text.primary,
              fontSize: responsive.font.xs,
              fontWeight: responsive.fontWeight.semibold,
              padding: 0,
            }}
          >
            {l10n.comments.reply}
          </button>
        </div>
      </div>
      <CommentLikeButton
        commentId={reply.id}
        likes={reply.likes}
        size={responsive.icon.xs}
        onToggle={liked => onLike(reply, liked)}
      />
    </div>
  );
};
